import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getTenantId } from "@/lib/auth";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import {
  Package,
  MapPin,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Eye,
  AlertTriangle,
  CheckCircle,
  Clock,
  Boxes,
  Truck,
  Building2,
  Activity,
  Target,
  Users
} from "lucide-react";
import {
  insertStockAreaSchema,
  insertStockPileSchema,
  insertStockAllocationSchema,
  createStockAreaSchema,
  createStockPileSchema,
  type StockArea,
  type StockPile,
  type StockAllocation,
  type Order,
  type Client,
  type InsertStockArea,
  type InsertStockPile,
  type InsertStockAllocation
} from "@shared/schema";
import { z } from "zod";
import GeoFenceMap from "@/components/geo-fence-map";

interface StockOverview {
  area: StockArea;
  piles: (StockPile & { allocatedQuantity: number; availableQuantity: number })[];
  totalCapacity: number;
  totalCurrentStock: number;
  totalAllocated: number;
  totalAvailable: number;
}

interface AllocationWithDetails extends StockAllocation {
  order?: Order;
  pile?: StockPile;
  area?: StockArea;
}

// Access allowance schema for truck limits per order
const accessAllowanceSchema = z.object({
  orderId: z.number(),
  maxTrucks: z.number().min(1).max(20),
  currentlyAllocated: z.number().default(0),
  timeSlot: z.string().optional(),
  notes: z.string().optional(),
});

type AccessAllowance = z.infer<typeof accessAllowanceSchema>;

export default function StockManagementPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  const [selectedPile, setSelectedPile] = useState<number | null>(null);
  const [showCreateAreaDialog, setShowCreateAreaDialog] = useState(false);
  const [showCreatePileDialog, setShowCreatePileDialog] = useState(false);
  const [showCreateAllocationDialog, setShowCreateAllocationDialog] = useState(false);
  const [showAccessAllowanceDialog, setShowAccessAllowanceDialog] = useState(false);
  const [showEditAreaDialog, setShowEditAreaDialog] = useState(false);
  const [showEditPileDialog, setShowEditPileDialog] = useState(false);
  const [showQuickClientDialog, setShowQuickClientDialog] = useState(false);
  const [showQuickAreaDialog, setShowQuickAreaDialog] = useState(false);
  const [editingArea, setEditingArea] = useState<StockArea | null>(null);
  const [editingPile, setEditingPile] = useState<StockPile | null>(null);

  // Geo-fencing state
  const [selectedAreaOnMap, setSelectedAreaOnMap] = useState<number | null>(null);
  const [currentGeoFence, setCurrentGeoFence] = useState<GeoJSON.Polygon | null>(null);
  const [currentCenter, setCurrentCenter] = useState<{ lat: number; lng: number } | null>(null);

  const { toast } = useToast();

  //  current tenant ID from logged-in user
  const currentTenantId = getTenantId() || "";

  // Form for creating/editing stock area
  const areaForm = useForm<InsertStockArea>({
    resolver: zodResolver(createStockAreaSchema),
    defaultValues: {
      tenantId: currentTenantId,
      facilityId: 1,
      areaCode: "",
      name: "",
      description: "",
      areaType: "storage",
      maxCapacity: "1000",
      capacityUnit: "kg",
      status: "active",
      accessLevel: "standard",
      geoFence: { type: "Polygon", coordinates: [[]] },
      centerLatitude: null,
      centerLongitude: null,
    }
  });

  

  // Quick area form for inline creation
  const quickAreaForm = useForm<InsertStockArea>({
    resolver: zodResolver(createStockAreaSchema),
    defaultValues: {
      tenantId: currentTenantId,
      facilityId: 1,
      areaCode: "",
      name: "",
      description: "",
      areaType: "storage",
      maxCapacity: "1000",
      capacityUnit: "kg",
      status: "active",
      accessLevel: "standard",
      geoFence: { type: "Polygon", coordinates: [[]] },
      centerLatitude: null,
      centerLongitude: null,
    }
  });

  // Form for creating/editing stock pile
  const pileForm = useForm<InsertStockPile>({
    resolver: zodResolver(createStockPileSchema),
    defaultValues: {
      tenantId: currentTenantId,
      areaId: undefined as any,
      pileCode: "",
      name: "",
      description: "",
      materialType: "",
      materialGrade: "",
      clientId: undefined,
      clientName: "",
      currentQuantity: "0",
      unit: "kg",
      status: "active",
      createdDate: new Date() as any, // Required field
    }
  });

  // Form for creating stock allocation
  const allocationForm = useForm<InsertStockAllocation>({
    resolver: zodResolver(insertStockAllocationSchema),
    defaultValues: {
      tenantId: currentTenantId,
      orderId: 0,
      pileId: 0,
      allocatedQuantity: "0",
      deliveredQuantity: "0",
      remainingQuantity: "0",
      unit: "kg",
      priority: 1,
      status: "allocated",
      allocatedBy: "system",
    }
  });

  // Form for access allowance
  const allowanceForm = useForm<AccessAllowance>({
    resolver: zodResolver(accessAllowanceSchema),
    defaultValues: {
      orderId: 0,
      maxTrucks: 1,
      currentlyAllocated: 0,
    }
  });

  // Quick client form for inline creation
  const quickClientForm = useForm<{ customerName: string; customerCode: string }>({
    defaultValues: {
      customerName: "",
      customerCode: "",
    }
  });

  // Fetch stock overview data
  const { data: stockOverview = [], isLoading: overviewLoading, error: overviewError } = useQuery<StockOverview[]>({
    queryKey: ['/api/stock/overview'],
  });

  // Fetch stock areas
  const { data: stockAreas = [], isLoading: areasLoading } = useQuery<StockArea[]>({
    queryKey: ['/api/stock/areas'],
  });

  // Fetch stock piles filtered by area
  const { data: stockPiles = [], isLoading: pilesLoading } = useQuery<StockPile[]>({
    queryKey: selectedArea ? ['/api/stock/piles', { areaId: selectedArea }] : ['/api/stock/piles'],
  });

  // Fetch stock allocations
  const { data: stockAllocations = [], isLoading: allocationsLoading } = useQuery<AllocationWithDetails[]>({
    queryKey: ['/api/stock/allocations'],
  });

  // Fetch orders for client information and allocation
  const { data: orders = [] } = useQuery<Order[]>({
    queryKey: ['/api/orders'],
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ['/api/clients'],
  });

  // Fetch access allowances
  const { data: accessAllowances = [] } = useQuery<AccessAllowance[]>({
    queryKey: ['/api/orders/access-allowances'],
  });

  // Create stock area mutation
  const createAreaMutation = useMutation({
    mutationFn: (areaData: InsertStockArea) => apiRequest('POST', '/api/stock/areas', areaData),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock area created successfully" });
      setShowCreateAreaDialog(false);
      areaForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/areas'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create stock area",
        variant: "destructive"
      });
    }
  });

  // Quick area creation mutation for pile form
  const createQuickAreaMutation = useMutation({
    mutationFn: (areaData: InsertStockArea) => apiRequest('POST', '/api/stock/areas', areaData),
    onSuccess: (newArea: StockArea) => {
      toast({ title: "Success", description: "Area created and selected" });
      setShowQuickAreaDialog(false);
      quickAreaForm.reset();
      // Set the newly created area in the pile form
      pileForm.setValue('areaId', newArea.id);
      queryClient.invalidateQueries({ queryKey: ['/api/stock/areas'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create area",
        variant: "destructive"
      });
    }
  });

  // Create stock pile mutation
  const createPileMutation = useMutation({
    mutationFn: async (pileData: InsertStockPile) => {
      console.log(' Mutation function called with:', pileData);
      try {
        const result = await apiRequest('POST', '/api/stock/piles', pileData);
        console.log(' API request succeeded:', result);
        return result;
      } catch (error) {
        console.error(' API request failed:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Mutation onSuccess called:', data);
      toast({ title: "Success", description: "Stock pile created successfully" });
      setShowCreatePileDialog(false);
      pileForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/areas'] });
    },
    onError: (error: any) => {
      console.error('Mutation onError called:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create stock pile",
        variant: "destructive"
      });
    }
  });

  // Create stock allocation mutation
  const createAllocationMutation = useMutation({
    mutationFn: (allocationData: InsertStockAllocation) => apiRequest('POST', '/api/stock/allocations', allocationData),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock allocation created successfully" });
      setShowCreateAllocationDialog(false);
      allocationForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/allocations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create stock allocation",
        variant: "destructive"
      });
    }
  });

  // Update access allowance mutation
  const updateAllowanceMutation = useMutation({
    mutationFn: ({ orderId, data }: { orderId: number; data: AccessAllowance }) =>
      apiRequest(`/api/orders/${orderId}/access-allowance`, 'PUT', data),
    onSuccess: () => {
      toast({ title: "Success", description: "Access allowance updated successfully" });
      setShowAccessAllowanceDialog(false);
      allowanceForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/orders/access-allowances'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update access allowance",
        variant: "destructive"
      });
    }
  });

  // Delete allocation mutation
  const deleteAllocationMutation = useMutation({
    mutationFn: (allocationId: number) => apiRequest('DELETE', `/api/stock/allocations/${allocationId}`),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock allocation deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/allocations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete stock allocation",
        variant: "destructive"
      });
    }
  });

  // Update stock area mutation
  const updateAreaMutation = useMutation({
    mutationFn: ({ areaId, areaData }: { areaId: number; areaData: InsertStockArea }) =>
      apiRequest('PUT', `/api/stock/areas/${areaId}`, areaData),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock area updated successfully" });
      setShowEditAreaDialog(false);
      setEditingArea(null);
      areaForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/areas'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update stock area",
        variant: "destructive"
      });
    }
  });

  const updatePileMutation = useMutation({
    mutationFn: ({ pileId, pileData }: { pileId: number; pileData: InsertStockPile }) =>
      apiRequest('PUT', `/api/stock/piles/${pileId}`, pileData),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock pile updated successfully" });
      setShowEditPileDialog(false);
      setEditingPile(null);
      pileForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/areas'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/overview'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update stock pile",
        variant: "destructive"
      });
    }
  });

  // Quick client creation mutation
  const createQuickClientMutation = useMutation({
    mutationFn: (clientData: { customerName: string; customerCode: string }) => {
      const clientCreateData = {
        tenantId: currentTenantId,
        name: clientData.customerName,
        contactPerson: "",
        email: "",
        phone: "",
        address: "",
        registrationNumber: clientData.customerCode,
        status: "active",
        type: "company"
      };
      return apiRequest('POST', '/api/clients', clientCreateData);
    },
    onSuccess: (newClient: any) => {
      toast({ title: "Success", description: "Client created and selected" });
      setShowQuickClientDialog(false);
      quickClientForm.reset();
      // Set the newly created client in the pile form
      pileForm.setValue('clientId', newClient.id);
      pileForm.setValue('clientName', newClient.name);
      queryClient.invalidateQueries({ queryKey: ['/api/clients'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create client",
        variant: "destructive"
      });
    }
  });

  const onCreateArea = (data: InsertStockArea) => {
    console.log('Creating area with data:', data);
    console.log('Form errors:', areaForm.formState.errors);
    const areaData = {
      ...data,
      geoFence: data.geoFence || {},
      centerLatitude: data.centerLatitude || null,
      centerLongitude: data.centerLongitude || null,
    };
    createAreaMutation.mutate(areaData);
  };

  // Quick area creation handler for pile form
  const onCreateQuickArea = (data: InsertStockArea) => {
    console.log('Creating quick area with data:', data);
    console.log('Quick form errors:', quickAreaForm.formState.errors);
    const areaData = {
      ...data,
      geoFence: {},
      centerLatitude: null,
      centerLongitude: null,
    };
    createQuickAreaMutation.mutate(areaData);
  };

  const onCreatePile = (data: InsertStockPile) => {
    console.log('Creating pile with data:', data);

    // Validate areaId is set
    if (!data.areaId) {
      toast({
        title: "Validation Error",
        description: "Please select an area for the pile",
        variant: "destructive"
      });
      return;
    }

    console.log('Submitting pile data to API...');
    createPileMutation.mutate(data);
  };

  const onCreateAllocation = (data: InsertStockAllocation) => {
    const allocationData = {
      ...data,
      remainingQuantity: data.allocatedQuantity,
    };
    createAllocationMutation.mutate(allocationData);
  };

  const onUpdateAllowance = (data: AccessAllowance) => {
    updateAllowanceMutation.mutate({ orderId: data.orderId, data });
  };

  // Quick client creation handler
  const onCreateQuickClient = (data: { customerName: string; customerCode: string }) => {
    console.log('Creating quick client with data:', data);
    console.log('Quick client form errors:', quickClientForm.formState.errors);
    createQuickClientMutation.mutate(data);
  };

  // Update area handler
  const onUpdateArea = (data: InsertStockArea) => {
    if (editingArea) {
      console.log('Updating area with data:', data);
      console.log('Edit form errors:', areaForm.formState.errors);
      updateAreaMutation.mutate({ areaId: editingArea.id, areaData: data });
    }
  };

  const onUpdatePile = (data: InsertStockPile) => {
    if (editingPile) {
      console.log('Updating pile with data:', data);
      console.log('Edit form errors:', pileForm.formState.errors);
      updatePileMutation.mutate({ pileId: editingPile.id, pileData: data });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'allocated': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'partial_delivery': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'quarantined': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatQuantity = (quantity: string | number, unit: string) => {
    const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
    if (isNaN(num)) return '0 ' + unit;
    return num.toLocaleString() + ' ' + unit;
  };

  const getOrderAccessAllowance = (orderId: number) => {
    return accessAllowances.find(a => a.orderId === orderId);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="stock-management-title">
            <Package className="h-8 w-8" />
            Stock Management
          </h1>
          <p className="text-muted-foreground">Manage stock areas, piles, allocations, and access allowances</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateAreaDialog(true)} data-testid="button-new-area">
            <MapPin className="h-4 w-4 mr-2" />
            New Area
          </Button>
          <Button onClick={() => setShowCreatePileDialog(true)} data-testid="button-new-pile">
            <Plus className="h-4 w-4 mr-2" />
            New Pile
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="areas" data-testid="tab-areas">
            <MapPin className="h-4 w-4 mr-2" />
            Areas
          </TabsTrigger>
          <TabsTrigger value="piles" data-testid="tab-piles">
            <Package className="h-4 w-4 mr-2" />
            Piles
          </TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map">
            <MapPin className="h-4 w-4 mr-2" />
            Map View
          </TabsTrigger>
          <TabsTrigger value="allocations" data-testid="tab-allocations">
            <Boxes className="h-4 w-4 mr-2" />
            Allocations
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Areas</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stockAreas.length}</div>
                <p className="text-xs text-muted-foreground">Stock storage areas</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Piles</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stockPiles.length}</div>
                <p className="text-xs text-muted-foreground">Material piles</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Allocations</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stockAllocations.filter(a => a.status === 'allocated').length}
                </div>
                <p className="text-xs text-muted-foreground">Pending deliveries</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Truck Utilization</CardTitle>
                <Truck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {accessAllowances.reduce((sum, a) => sum + a.currentlyAllocated, 0)} / {accessAllowances.reduce((sum, a) => sum + a.maxTrucks, 0)}
                </div>
                <p className="text-xs text-muted-foreground">Trucks allocated / max</p>
              </CardContent>
            </Card>
          </div>

          {overviewLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading stock overview...</p>
                </div>
              </CardContent>
            </Card>
          ) : stockOverview.length > 0 ? (
            <div className="space-y-6">
              {stockOverview.map((overview) => (
                <Link key={overview.area.id} href={`/stock-management/areas/${overview.area.id}`}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`card-area-${overview.area.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <MapPin className="h-5 w-5" />
                            {overview.area.name}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {overview.area.areaCode} • {overview.area.areaType}
                          </p>
                        </div>
                        <Badge variant="outline" className={getStatusColor(overview.area.status)}>
                          {overview.area.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {formatQuantity(overview.totalCurrentStock, overview.area.capacityUnit || 'kg')}
                          </div>
                          <div className="text-sm text-muted-foreground">Current Stock</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {formatQuantity(overview.totalAvailable, overview.area.capacityUnit || 'kg')}
                          </div>
                          <div className="text-sm text-muted-foreground">Available</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {formatQuantity(overview.totalAllocated, overview.area.capacityUnit || 'kg')}
                          </div>
                          <div className="text-sm text-muted-foreground">Allocated</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-600">
                            {formatQuantity(overview.totalCapacity || 0, overview.area.capacityUnit || 'kg')}
                          </div>
                          <div className="text-sm text-muted-foreground">Capacity</div>
                        </div>
                      </div>

                      {overview.totalCapacity > 0 && (
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Utilization</span>
                            <span>{((overview.totalCurrentStock / overview.totalCapacity) * 100).toFixed(1)}%</span>
                          </div>
                          <Progress value={(overview.totalCurrentStock / overview.totalCapacity) * 100} />
                        </div>
                      )}

                      {overview.piles.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-semibold">Piles in this area:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {overview.piles.map((pile) => (
                              <div key={pile.id} className="border rounded-lg p-3">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <div className="font-medium">{pile.name}</div>
                                    <div className="text-xs text-muted-foreground">{pile.pileCode}</div>
                                  </div>
                                  <Badge variant="outline" className={getStatusColor(pile.status)}>
                                    {pile.status}
                                  </Badge>
                                </div>
                                <div className="text-sm space-y-1">
                                  <div>Material: {pile.materialType}</div>
                                  <div>Current: {formatQuantity(pile.currentQuantity, pile.unit)}</div>
                                  <div>Available: {formatQuantity(pile.availableQuantity, pile.unit)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Package className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Stock Areas Found</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Get started by creating your first stock area and adding material piles.
                </p>
                <Button onClick={() => setShowCreateAreaDialog(true)} data-testid="button-create-first-area">
                  <MapPin className="h-4 w-4 mr-2" />
                  Create First Area
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Areas Tab */}
        <TabsContent value="areas" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Stock Areas</CardTitle>
            </CardHeader>
            <CardContent>
              {areasLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading areas...</p>
                </div>
              ) : stockAreas.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Area Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Capacity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockAreas.map((area) => (
                      <TableRow key={area.id}>
                        <TableCell className="font-medium">{area.areaCode}</TableCell>
                        <TableCell>{area.name}</TableCell>
                        <TableCell className="capitalize">{area.areaType}</TableCell>
                        <TableCell>
                          {area.maxCapacity ? formatQuantity(area.maxCapacity, area.capacityUnit || 'kg') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusColor(area.status)}>
                            {area.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingArea(area);
                                areaForm.reset({
                                  tenantId: area.tenantId,
                                  facilityId: area.facilityId || 1,
                                  areaCode: area.areaCode,
                                  name: area.name,
                                  description: area.description || "",
                                  areaType: area.areaType,
                                  maxCapacity: area.maxCapacity || "",
                                  capacityUnit: area.capacityUnit || "kg",
                                  status: area.status,
                                  accessLevel: area.accessLevel || "standard",
                                  geoFence: area.geoFence || { type: "Polygon", coordinates: [[]] },
                                  centerLatitude: area.centerLatitude || null,
                                  centerLongitude: area.centerLongitude || null,
                                });
                                setShowEditAreaDialog(true);
                              }}
                              data-testid={`button-edit-area-${area.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedArea(area.id);
                                setActiveTab("piles");
                              }}
                              data-testid={`button-view-piles-${area.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Areas Found</h3>
                  <p className="text-muted-foreground mb-4">Create your first stock area to get started.</p>
                  <Button onClick={() => setShowCreateAreaDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Area
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Piles Tab */}
        <TabsContent value="piles" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Stock Piles</CardTitle>
                <Select value={selectedArea?.toString() || "all"} onValueChange={(value) => setSelectedArea(value === "all" ? null : parseInt(value))}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Areas</SelectItem>
                    {stockAreas.map((area) => (
                      <SelectItem key={area.id} value={area.id.toString()}>
                        {area.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {pilesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading piles...</p>
                </div>
              ) : stockPiles.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pile Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Current Stock</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockPiles.map((pile) => {
                      const available = parseFloat(pile.currentQuantity.toString()) - parseFloat(pile.reservedQuantity.toString());
                      return (
                        <TableRow key={pile.id}>
                          <TableCell className="font-medium">{pile.pileCode}</TableCell>
                          <TableCell>{pile.name}</TableCell>
                          <TableCell>
                            <div>
                              <div>{pile.materialType}</div>
                              {pile.materialGrade && (
                                <div className="text-xs text-muted-foreground">Grade: {pile.materialGrade}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{formatQuantity(pile.currentQuantity, pile.unit)}</TableCell>
                          <TableCell>{formatQuantity(available, pile.unit)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusColor(pile.status)}>
                              {pile.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {

                                  setEditingPile(pile);
                                  pileForm.reset({
                                    tenantId: pile.tenantId,
                                    areaId: pile.areaId,
                                    pileCode: pile.pileCode,
                                    name: pile.name,
                                    description: pile.description || "",
                                    materialType: pile.materialType,
                                    materialGrade: pile.materialGrade || "",
                                    currentQuantity: pile.currentQuantity.toString(),
                                    unit: pile.unit,
                                    status: pile.status,
                                    createdDate: pile.createdDate,
                                  });
                                  setShowEditPileDialog(true);
                                }}
                                data-testid={`button-edit-pile-${pile.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Piles Found</h3>
                  <p className="text-muted-foreground mb-4">
                    {selectedArea ? "No piles in this area." : "Create your first stock pile to get started."}
                  </p>
                  <Button onClick={() => setShowCreatePileDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Pile
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Map View Tab */}
        <TabsContent value="map" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Geographic View</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <GeoFenceMap
                  areas={stockAreas}
                  selectedAreaId={selectedAreaOnMap}
                  onAreaSelect={setSelectedAreaOnMap}
                  onGeoFenceChange={setCurrentGeoFence}
                  onCenterChange={setCurrentCenter}
                  editMode={false}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Allocations Tab */}
        <TabsContent value="allocations" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Stock Allocations</CardTitle>
                <div className="flex gap-2">
                  <Button onClick={() => setShowAccessAllowanceDialog(true)} variant="outline" data-testid="button-manage-allowances">
                    <Truck className="h-4 w-4 mr-2" />
                    Manage Access Allowances
                  </Button>
                  <Button onClick={() => setShowCreateAllocationDialog(true)} data-testid="button-new-allocation">
                    <Plus className="h-4 w-4 mr-2" />
                    New Allocation
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {allocationsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading allocations...</p>
                </div>
              ) : stockAllocations.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Pile</TableHead>
                      <TableHead>Allocated</TableHead>
                      <TableHead>Delivered</TableHead>
                      <TableHead>Remaining</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Truck Allowance</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockAllocations.map((allocation) => {
                      const allowance = allocation.orderId ? getOrderAccessAllowance(allocation.orderId) : null;
                      return (
                        <TableRow key={allocation.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {allocation.order?.orderNumber || `Order #${allocation.orderId}`}
                              </div>
                              {allocation.order?.product && (
                                <div className="text-xs text-muted-foreground">{allocation.order.product}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{allocation.pile?.name || `Pile #${allocation.pileId}`}</div>
                              <div className="text-xs text-muted-foreground">
                                {allocation.pile?.pileCode} • {allocation.pile?.materialType}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{formatQuantity(allocation.allocatedQuantity, allocation.unit)}</TableCell>
                          <TableCell>{formatQuantity(allocation.deliveredQuantity, allocation.unit)}</TableCell>
                          <TableCell>{formatQuantity(allocation.remainingQuantity, allocation.unit)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusColor(allocation.status)}>
                              {allocation.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {allowance ? (
                              <div className="text-sm">
                                <div>{allowance.currentlyAllocated} / {allowance.maxTrucks} trucks</div>
                                <Progress
                                  value={(allowance.currentlyAllocated / allowance.maxTrucks) * 100}
                                  className="w-16 h-2 mt-1"
                                />
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Not set</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" data-testid={`button-delete-allocation-${allocation.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Allocation</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this stock allocation? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteAllocationMutation.mutate(allocation.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Boxes className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Allocations Found</h3>
                  <p className="text-muted-foreground mb-4">Create your first stock allocation to get started.</p>
                  <Button onClick={() => setShowCreateAllocationDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Allocation
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Area Dialog */}
      <Dialog open={showCreateAreaDialog} onOpenChange={setShowCreateAreaDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Stock Area</DialogTitle>
          </DialogHeader>
          <Form {...areaForm}>
            <form onSubmit={areaForm.handleSubmit(onCreateArea)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={areaForm.control}
                  name="areaCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area Code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., A001" data-testid="input-area-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={areaForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Main Storage Area" data-testid="input-area-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={areaForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Description of the area" data-testid="input-area-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={areaForm.control}
                  name="areaType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-area-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="storage">Storage</SelectItem>
                          <SelectItem value="staging">Staging</SelectItem>
                          <SelectItem value="loading">Loading</SelectItem>
                          <SelectItem value="quarantine">Quarantine</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={areaForm.control}
                  name="maxCapacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Capacity</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} placeholder="1000" data-testid="input-area-capacity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={areaForm.control}
                  name="capacityUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-area-unit">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="tons">Tons</SelectItem>
                          <SelectItem value="m3">Cubic Meters</SelectItem>
                          <SelectItem value="liters">Liters</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateAreaDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createAreaMutation.isPending} data-testid="button-create-area">
                  {createAreaMutation.isPending ? 'Creating...' : 'Create Area'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Create Pile Dialog */}
      <Dialog open={showCreatePileDialog} onOpenChange={setShowCreatePileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Stock Pile</DialogTitle>
          </DialogHeader>
          <Form {...pileForm}>
            <form onSubmit={(e) => {
              console.log('📝 Form submit event triggered');
              pileForm.handleSubmit(
                (data) => {
                  console.log('✅ Form validation passed, calling onCreatePile');
                  onCreatePile(data);
                },
                (errors) => {
                  console.error(' Form validation failed:', errors);
                  toast({
                    title: "Validation Error",
                    description: "Please fill in all required fields correctly",
                    variant: "destructive"
                  });
                }
              )(e);
            }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="areaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area *</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        value={field.value ? field.value.toString() : undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-pile-area">
                            <SelectValue placeholder="Select area" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {stockAreas.length === 0 ? (
                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                              No areas available. Create one first.
                            </div>
                          ) : (
                            stockAreas.map((area) => (
                              <SelectItem key={area.id} value={area.id.toString()}>
                                {area.name}
                              </SelectItem>
                            ))
                          )}
                          <div className="border-t mt-2 pt-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-primary hover:text-primary"
                              onClick={() => setShowQuickAreaDialog(true)}
                              data-testid="button-add-new-area"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add New Area
                            </Button>
                          </div>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="pileCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pile Code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., A001-P1" data-testid="input-pile-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={pileForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Iron Ore Pile 1" data-testid="input-pile-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="materialType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Material Type</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Iron Ore, Coal, Sand" data-testid="input-pile-material" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="materialGrade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Material Grade</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Premium, Standard" data-testid="input-pile-grade" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Client Association */}
              <FormField
                control={pileForm.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Client (Optional)</FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowQuickClientDialog(true)}
                        data-testid="button-create-client"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New Client
                      </Button>
                    </div>
                    <Select onValueChange={(value) => {
                      const clientId = value === "none" ? undefined : parseInt(value);
                      field.onChange(clientId);
                      // Update client name when client changes
                      const selectedClient = clients.find(client => client.id === clientId);
                      if (selectedClient) {
                        pileForm.setValue('clientName', selectedClient.name || '');
                      } else {
                        pileForm.setValue('clientName', '');
                      }
                    }} value={field.value ? field.value.toString() : "none"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-pile-client">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No specific client</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="currentQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Initial Quantity</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} placeholder="1000" data-testid="input-pile-quantity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-pile-unit">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="tons">Tons</SelectItem>
                          <SelectItem value="m3">Cubic Meters</SelectItem>
                          <SelectItem value="liters">Liters</SelectItem>
                          <SelectItem value="pieces">Pieces</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={pileForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Description of the pile" data-testid="input-pile-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreatePileDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createPileMutation.isPending} data-testid="button-create-pile">
                  {createPileMutation.isPending ? 'Creating...' : 'Create Pile'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Create Allocation Dialog */}
      <Dialog open={showCreateAllocationDialog} onOpenChange={setShowCreateAllocationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Stock Allocation</DialogTitle>
          </DialogHeader>
          <Form {...allocationForm}>
            <form onSubmit={allocationForm.handleSubmit(onCreateAllocation)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={allocationForm.control}
                  name="orderId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Order</FormLabel>
                      <Select onValueChange={(value) => field.onChange(parseInt(value))} defaultValue={field.value.toString()}>
                        <FormControl>
                          <SelectTrigger data-testid="select-allocation-order">
                            <SelectValue placeholder="Select order" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {orders.map((order) => (
                            <SelectItem key={order.id} value={order.id.toString()}>
                              {order.orderNumber} - {order.product}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="pileId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pile</FormLabel>
                      <Select onValueChange={(value) => field.onChange(parseInt(value))} defaultValue={field.value.toString()}>
                        <FormControl>
                          <SelectTrigger data-testid="select-allocation-pile">
                            <SelectValue placeholder="Select pile" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {stockPiles.map((pile) => (
                            <SelectItem key={pile.id} value={pile.id.toString()}>
                              {pile.name} ({pile.pileCode})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={allocationForm.control}
                  name="allocatedQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Allocated Quantity</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} placeholder="100" data-testid="input-allocation-quantity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-allocation-unit">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="tons">Tons</SelectItem>
                          <SelectItem value="m3">Cubic Meters</SelectItem>
                          <SelectItem value="liters">Liters</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <Select onValueChange={(value) => field.onChange(parseInt(value))} defaultValue={field.value.toString()}>
                        <FormControl>
                          <SelectTrigger data-testid="select-allocation-priority">
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1">High (1)</SelectItem>
                          <SelectItem value="2">Medium-High (2)</SelectItem>
                          <SelectItem value="3">Medium (3)</SelectItem>
                          <SelectItem value="4">Medium-Low (4)</SelectItem>
                          <SelectItem value="5">Low (5)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={allocationForm.control}
                name="scheduledDeliveryDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scheduled Delivery Date</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value ? format(new Date(field.value), "yyyy-MM-dd'T'HH:mm") : ""}
                        onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : null)}
                        data-testid="input-allocation-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={allocationForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Additional notes about this allocation" data-testid="input-allocation-notes" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateAllocationDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createAllocationMutation.isPending} data-testid="button-create-allocation">
                  {createAllocationMutation.isPending ? 'Creating...' : 'Create Allocation'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Access Allowance Dialog */}
      <Dialog open={showAccessAllowanceDialog} onOpenChange={setShowAccessAllowanceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Access Allowances</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Current Allowances */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Current Access Allowances</h3>
              {accessAllowances.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Max Trucks</TableHead>
                      <TableHead>Currently Allocated</TableHead>
                      <TableHead>Utilization</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessAllowances.map((allowance) => {
                      const order = orders.find(o => o.id === allowance.orderId);
                      return (
                        <TableRow key={allowance.orderId}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{order?.orderNumber || `Order #${allowance.orderId}`}</div>
                              {order?.product && <div className="text-xs text-muted-foreground">{order.product}</div>}
                            </div>
                          </TableCell>
                          <TableCell>{allowance.maxTrucks}</TableCell>
                          <TableCell>{allowance.currentlyAllocated}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={(allowance.currentlyAllocated / allowance.maxTrucks) * 100} className="w-16" />
                              <span className="text-xs">
                                {Math.round((allowance.currentlyAllocated / allowance.maxTrucks) * 100)}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                allowanceForm.reset(allowance);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 border rounded-lg">
                  <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Access Allowances Set</h3>
                  <p className="text-muted-foreground">Set truck limits for orders to manage site access.</p>
                </div>
              )}
            </div>

            {/* Create/Edit Form */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Set Access Allowance</h3>
              <Form {...allowanceForm}>
                <form onSubmit={allowanceForm.handleSubmit(onUpdateAllowance)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={allowanceForm.control}
                      name="orderId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Order</FormLabel>
                          <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value.toString()}>
                            <FormControl>
                              <SelectTrigger data-testid="select-allowance-order">
                                <SelectValue placeholder="Select order" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {orders.map((order) => (
                                <SelectItem key={order.id} value={order.id.toString()}>
                                  {order.orderNumber} - {order.product}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={allowanceForm.control}
                      name="maxTrucks"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Maximum Trucks</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="1"
                              max="20"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value))}
                              data-testid="input-allowance-max-trucks"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={allowanceForm.control}
                      name="timeSlot"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Time Slot (Optional)</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <FormControl>
                              <SelectTrigger data-testid="select-allowance-timeslot">
                                <SelectValue placeholder="Select time slot" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="">Any Time</SelectItem>
                              <SelectItem value="morning">Morning (06:00 - 12:00)</SelectItem>
                              <SelectItem value="afternoon">Afternoon (12:00 - 18:00)</SelectItem>
                              <SelectItem value="evening">Evening (18:00 - 24:00)</SelectItem>
                              <SelectItem value="night">Night (00:00 - 06:00)</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={allowanceForm.control}
                      name="currentlyAllocated"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Currently Allocated</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value))}
                              data-testid="input-allowance-current"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={allowanceForm.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea {...field} placeholder="Special instructions or notes" data-testid="input-allowance-notes" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => {
                      setShowAccessAllowanceDialog(false);
                      allowanceForm.reset();
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updateAllowanceMutation.isPending} data-testid="button-save-allowance">
                      {updateAllowanceMutation.isPending ? 'Saving...' : 'Save Allowance'}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Area Creation Dialog */}
      <Dialog open={showQuickAreaDialog} onOpenChange={setShowQuickAreaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Add Area</DialogTitle>
          </DialogHeader>
          <Form {...quickAreaForm}>
            <form onSubmit={quickAreaForm.handleSubmit(onCreateQuickArea)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={quickAreaForm.control}
                  name="areaCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area Code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., A001" data-testid="input-quick-area-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={quickAreaForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., ABC ZONE" data-testid="input-quick-area-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={quickAreaForm.control}
                name="areaType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-quick-area-type">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="storage">Storage</SelectItem>
                        <SelectItem value="loading">Loading</SelectItem>
                        <SelectItem value="staging">Staging</SelectItem>
                        <SelectItem value="quarantine">Quarantine</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowQuickAreaDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createQuickAreaMutation.isPending} data-testid="button-create-quick-area">
                  {createQuickAreaMutation.isPending ? 'Creating...' : 'Create & Select'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Pile Dialog */}
      <Dialog open={showEditPileDialog} onOpenChange={setShowEditPileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stock Pile</DialogTitle>
          </DialogHeader>

          {/* If you want to prefill when dialog opens */}
          {/* useEffect(() => {
      if (editingPile) {
        editPileForm.reset({
          areaId: editingPile.areaId,
          pileCode: editingPile.pileCode || "",
          name: editingPile.name || "",
          materialType: editingPile.materialType || "",
          materialGrade: editingPile.materialGrade || "",
          clientId: editingPile.clientId ?? undefined,
          clientName: editingPile.clientName || "",
          currentQuantity: editingPile.currentQuantity ?? 0,
          unit: editingPile.unit || "tons",
          description: editingPile.description || "",
        });
      }
    }, [editingPile, showEditPileDialog]); */}

          <Form {...pileForm}>
            <form
              onSubmit={(e) => {
                pileForm.handleSubmit(
                  (data) => onUpdatePile({ id: editingPile?.id, ...data }),
                  (errors) => {
                    console.error('Edit pile validation failed:', errors);
                    toast({
                      title: "Validation Error",
                      description: "Please fix the highlighted fields.",
                      variant: "destructive",
                    });
                  }
                )(e);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="areaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area *</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        value={
                          field.value !== undefined && field.value !== null
                            ? String(field.value)
                            : undefined
                        }
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-pile-area">
                            <SelectValue placeholder="Select area" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {stockAreas.length === 0 ? (
                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                              No areas available. Create one first.
                            </div>
                          ) : (
                            stockAreas.map((area) => (
                              <SelectItem key={area.id} value={String(area.id)}>
                                {area.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="pileCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pile Code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., A001-P1" data-testid="input-edit-pile-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={pileForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Iron Ore Pile 1" data-testid="input-edit-pile-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="materialType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Material Type</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Iron Ore, Coal, Sand" data-testid="input-edit-pile-material" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="materialGrade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Material Grade</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Premium, Standard" data-testid="input-edit-pile-grade" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Client Association (optional) */}
              <FormField
                control={pileForm.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Client (Optional)</FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowQuickClientDialog(true)}
                        data-testid="button-edit-create-client"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New Client
                      </Button>
                    </div>
                    <Select
                      onValueChange={(value) => {
                        const clientId = value === "none" ? undefined : parseInt(value);
                        field.onChange(clientId);
                        // keep clientName in sync
                        const selectedClient = clients.find(c => c.id === clientId);
                        pileForm.setValue("clientName", selectedClient?.name || "");
                      }}
                      value={
                        field.value === undefined || field.value === null
                          ? "none"
                          : String(field.value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-pile-client">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No specific client</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={String(client.id)}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={pileForm.control}
                  name="currentQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quantity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value === "" ? "" : Number(e.target.value))}
                          placeholder="1000"
                          data-testid="input-edit-pile-quantity"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={pileForm.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-pile-unit">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="tons">Tons</SelectItem>
                          <SelectItem value="m3">Cubic Meters</SelectItem>
                          <SelectItem value="liters">Liters</SelectItem>
                          <SelectItem value="pieces">Pieces</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={pileForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Optional description..." data-testid="input-edit-pile-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditPileDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updatePileMutation.isPending}
                  data-testid="button-update-pile"
                >
                  {updatePileMutation.isPending ? 'Updating...' : 'Update Pile'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>


      {/* Edit Area Dialog */}
      <Dialog open={showEditAreaDialog} onOpenChange={setShowEditAreaDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Stock Area</DialogTitle>
          </DialogHeader>
          <Form {...areaForm}>
            <form onSubmit={areaForm.handleSubmit(onUpdateArea)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={areaForm.control}
                  name="areaCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area Code</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., A001" data-testid="input-edit-area-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={areaForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Main Storage Area" data-testid="input-edit-area-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={areaForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Optional description..." data-testid="input-edit-area-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={areaForm.control}
                  name="areaType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-area-type">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="storage">Storage</SelectItem>
                          <SelectItem value="loading">Loading</SelectItem>
                          <SelectItem value="staging">Staging</SelectItem>
                          <SelectItem value="restricted">Restricted</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={areaForm.control}
                  name="maxCapacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Capacity</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 1000" data-testid="input-edit-area-capacity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={areaForm.control}
                  name="capacityUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-area-unit">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="tons">tons</SelectItem>
                          <SelectItem value="m3">m³</SelectItem>
                          <SelectItem value="liters">liters</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEditAreaDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateAreaMutation.isPending} data-testid="button-update-area">
                  {updateAreaMutation.isPending ? 'Updating...' : 'Update Area'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Quick Client Creation Dialog */}
      <Dialog open={showQuickClientDialog} onOpenChange={setShowQuickClientDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
          </DialogHeader>
          <Form {...quickClientForm}>
            <form onSubmit={quickClientForm.handleSubmit(onCreateQuickClient)} className="space-y-4">
              <FormField
                control={quickClientForm.control}
                name="customerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., ABC Mining Corp" data-testid="input-client-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={quickClientForm.control}
                name="customerCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Code</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., ABC001" data-testid="input-client-code" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowQuickClientDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createQuickClientMutation.isPending} data-testid="button-create-quick-client">
                  {createQuickClientMutation.isPending ? 'Creating...' : 'Create & Select'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}